import React, { useState } from "react";
import { X, Loader, Link2, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const NAVY = "#0A2540";
const GREEN = "#047857";
const GREEN_DEEP = "#064E3B";
const GOLD = "#D4AF37";

/**
 * LinkClientModal — recruiter links a roster client row to an existing
 * Resmo user account by email. Once linked, the recruiter can "act on
 * behalf" of the client across the toolkit (tailor, strategy brief, etc.).
 *
 * Backend: POST /api/recruiter/clients/{client_id}/link
 *          body: { candidate_email }
 *          response: { ok, candidate_user_id, candidate_name, candidate_email }
 */
export default function LinkClientModal({ open, onClose, client, onLinked }) {
  const [email, setEmail] = useState(client?.email || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  React.useEffect(() => {
    if (open) {
      setEmail(client?.email || "");
      setError("");
      setDone(false);
      setBusy(false);
    }
  }, [open, client]);

  if (!open || !client) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !email.includes("@")) {
      setError("Need a valid email address.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/recruiter/clients/${client.id}/link`, {
        candidate_email: email,
      });
      setDone(true);
      onLinked?.({
        client_id: client.id,
        client_name: data.candidate_name || client.name,
        client_email: data.candidate_email,
        candidate_user_id: data.candidate_user_id,
      });
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || "Couldn't link the client.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(10, 37, 64, 0.6)" }}
         onClick={onClose}
         data-testid="link-client-modal">
      <div className="rm-card-static p-6 w-full max-w-md"
           onClick={(e) => e.stopPropagation()}
           style={{ background: "white", borderColor: NAVY, boxShadow: `5px 5px 0 0 ${GOLD}` }}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="font-mono-rm uppercase text-[10px] tracking-widest"
                 style={{ color: GREEN_DEEP, fontWeight: 700 }}>
              · Resmo Pro · Link client to account
            </div>
            <h2 className="font-display uppercase text-xl leading-tight mt-1"
                style={{ color: NAVY, letterSpacing: "-0.02em" }}>
              {done ? "Linked." : `Link ${client.name}`}
            </h2>
            {!done && (
              <p className="text-[12px] mt-1" style={{ color: "#475569" }}>
                Connect this roster client to their Resmo account so you can run the toolkit on their behalf. They must already have a Resmo account.
              </p>
            )}
          </div>
          <button onClick={onClose}
                  className="rm-btn rm-btn-ghost text-xs px-2 py-2"
                  data-testid="link-client-close">
            <X size={14} />
          </button>
        </div>

        {done ? (
          <div className="rm-card-static p-4 text-center"
               style={{ background: "#ECFDF5", borderColor: GREEN }}
               data-testid="link-client-success">
            <CheckCircle2 size={28} style={{ color: GREEN, margin: "0 auto" }} />
            <p className="text-sm mt-2" style={{ color: GREEN_DEEP }}>
              <strong>{email}</strong> is now linked. You'll see an "Act as this client" button on their tile.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="link-client-form">
            <div>
              <label className="rm-label text-[11px]">Candidate's Resmo email *</label>
              <input type="email" value={email}
                     onChange={(e) => setEmail(e.target.value)}
                     className="rm-input w-full"
                     placeholder="candidate@example.com"
                     data-testid="link-client-email"
                     required />
              <div className="text-[10px] mt-1" style={{ color: "#94A3B8" }}>
                Make sure they've signed up at Resmo first — we won't create an account from this dialog.
              </div>
            </div>

            {error && (
              <div className="rm-card-static p-2 text-xs font-mono-rm"
                   style={{ background: "#FEE2E2", borderColor: "#B91C1C", color: "#991B1B" }}
                   data-testid="link-client-error">
                · {error}
              </div>
            )}

            <div className="flex justify-end gap-2 flex-wrap">
              <button type="button" onClick={onClose}
                      className="rm-btn rm-btn-ghost text-xs px-3 py-2"
                      data-testid="link-client-cancel">
                Cancel
              </button>
              <button type="submit" disabled={busy}
                      className="rm-btn text-sm px-4 py-2"
                      style={{
                        background: GREEN, color: "white",
                        border: `2px solid ${GREEN_DEEP}`,
                        boxShadow: `3px 3px 0 0 ${NAVY}`, fontWeight: 700,
                      }}
                      data-testid="link-client-submit">
                {busy ? <Loader size={14} className="animate-spin" /> : <Link2 size={14} />}
                {busy ? "Linking…" : "Link to account"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
